import { useCallback, useEffect, useRef, useState } from 'react';
import { VoiceSession, SessionState } from '@/services/liveSession';
import { trpc } from '@/lib/trpc';

export type TranscriptEntry = { role: 'user' | 'assistant'; text: string; timestamp: number; };

type Options = {
  voice?: string;
  // Recibe el TEXTO transcripto por el navegador (Web Speech API)
  onProcessMessage: (text: string) => Promise<string>;
};

export function useLiveAudioSession({ voice = 'Lupe', onProcessMessage }: Options) {
  const [sessionState, setSessionState] = useState<SessionState>('idle');
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sessionRef = useRef<VoiceSession | null>(null);
  const synthesizeSpeech = trpc.performance.realtime.synthesizeSpeech.useMutation();

  const appendTranscript = useCallback((text: string, role: 'user' | 'assistant') => {
    setTranscript((prev) => [...prev, { role, text, timestamp: Date.now() }]);
  }, []);

  const startSession = useCallback(async () => {
    if (sessionRef.current) return;
    setError(null);

    // El permiso de micrófono lo pide SpeechRecognition al arrancar; el prompt
    // explícito acá mejora el mensaje de error si lo niegan.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      stream.getTracks().forEach((t) => t.stop()); // solo para el permiso
    } catch {
      setError('microphone_denied');
      return;
    }

    const session = new VoiceSession({
      onProcessMessage: async (text) => {
        const reply = await onProcessMessage(text);
        appendTranscript(reply, 'assistant');
        return reply;
      },
      onSynthesizeSpeech: async (text) => {
        const result = await synthesizeSpeech.mutateAsync({ text, voice });
        return { data: result.audio, mimeType: result.mimeType };
      },
      callbacks: {
        onStateChange: setSessionState,
        onTranscript: appendTranscript,
        onError: (msg) => setError(msg),
        onSpeakingStart: () => setIsSpeaking(true),
        onSpeakingEnd: () => setIsSpeaking(false),
      },
    });

    sessionRef.current = session;
    await session.start();
  }, [voice, onProcessMessage, appendTranscript]);

  const endSession = useCallback(() => {
    sessionRef.current?.destroy();
    sessionRef.current = null;
    setSessionState('idle');
    setIsSpeaking(false);
    setIsMuted(false);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      sessionRef.current?.mute(next);
      return next;
    });
  }, []);

  useEffect(() => () => { sessionRef.current?.destroy(); }, []);

  return { sessionState, transcript, isMuted, isSpeaking, error, startSession, endSession, toggleMute };
}
